"use client";

import CdnImage from "@/components/CdnImage";
import { Typewriter } from "react-simple-typewriter";
import { useLeadModalStore } from "@/stores/useLeadModalStore";
import { useT } from "@/hooks/useT";

export default function Hero() {
  const t = useT();
  const { open } = useLeadModalStore();
  // SSR-stable word arrays. Picking via viewport at runtime would shift the h1 after
  // hydration and triggered the audited Hero CLS. CSS visibility classes route the
  // correct Typewriter to each breakpoint without any layout toggle.
  const DESKTOP_ROTATING_WORDS = [
    t("copytrading.hero.rotating.desktop.1"),
    t("copytrading.hero.rotating.desktop.2"),
    t("copytrading.hero.rotating.desktop.3"),
  ];
  const MOBILE_ROTATING_WORDS = [
    t("copytrading.hero.rotating.mobile.1"),
    t("copytrading.hero.rotating.mobile.2"),
  ];

  // Always render 5 avatars for SSR consistency; hide extras via CSS on mobile
  const avatars = ["/frenpfp.png", "/frenpfp.png", "/frenpfp.png", "/frenpfp.png", "/frenpfp.png"];

  return (
    <section
      className="relative overflow-hidden bg-white dark:bg-[#0f0f12] pt-6 pb-14 px-4 sm:px-6 lg:px-12"
      aria-labelledby="hero-heading"
    >
      {/* Content */}
      <div className="relative z-10">
        {/* Badge — Block 0 */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center gap-2 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 px-3 py-1.5 rounded-[5px] text-xs sm:text-sm font-medium font-satoshi border border-gray-200 dark:border-[#1A1A1A]">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-[5px] h-2 w-2 bg-emerald-500" />
            </span>
            <span className="hidden sm:inline">{t("copytrading.hero.badge.long")}</span>
            <span className="sm:hidden">{t("copytrading.hero.badge.short")}</span>
          </div>
        </div>

        {/* Headline */}
        <div className="max-w-3xl mx-auto text-center">
          <h1
            id="hero-heading"
            className="text-3xl sm:text-[3rem] md:text-[3rem] font-bold text-gray-900 dark:text-white leading-tight mb-4 text-center"
          >
            {/* Desktop: single line */}
            <span className="hidden sm:block">{t("copytrading.hero.headline.desktop")}</span>

            {/* Mobile: line break */}
            <span className="block sm:hidden">
              {t("copytrading.hero.headline.mobilePrefix")}{" "}
              <span className="text-accent-primary">
                <Typewriter
                  words={MOBILE_ROTATING_WORDS}
                  loop={0}
                  cursor
                  cursorStyle="|"
                  typeSpeed={60}
                  deleteSpeed={40}
                  delaySpeed={1500}
                />
              </span>
            </span>

            {/* Desktop second line */}
            <span className="hidden mt-1 sm:block">
              {t("copytrading.hero.headline.desktopLikePrefix")}{" "}
              <span className="text-accent-primary">
                <Typewriter
                  words={DESKTOP_ROTATING_WORDS}
                  loop={0}
                  cursor
                  cursorStyle="|"
                  typeSpeed={60}
                  deleteSpeed={40}
                  delaySpeed={1500}
                />
              </span>
            </span>
          </h1>

          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 max-w-xl mx-auto">
            {t("copytrading.hero.subtitle")}
          </p>
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap justify-center gap-4 mt-2" role="group" aria-label="Call to action">
          <button
            onClick={open}
            className="group relative flex items-center gap-2 px-5 py-3 h-11 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-inter-tight font-bold transition-colors text-sm sm:text-base overflow-hidden hover:bg-gray-800 dark:hover:bg-gray-100"
            aria-label="Start Polymarket copy trading with FrenFlow bot"
          >
            <CdnImage
              src="/brand/frenflow_logo.png"
              alt="FrenFlow Polymarket copy trading bot logo"
              width={18}
              height={18}
              className="relative z-10 object-contain brightness-0 dark:brightness-100"
              aria-hidden="true"
            />
            <span className="relative z-10">{t("copytrading.hero.ctaPrimary")}</span>
          </button>
          <a
            href="#points"
            className="px-5 py-3 h-11 inline-flex items-center rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-white font-inter-tight font-semibold hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-sm sm:text-base"
          >
            {t("copytrading.hero.ctaSecondary")}
          </a>
        </div>

        {/* Subcopy */}
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">
          {t("copytrading.hero.subcopy")}
        </p>

        {/* Social proof */}
        <div className="relative z-20 mt-5 mb-[-20px] sm:mt-6 flex justify-center">
          <div className="bg-white dark:bg-[#18181D] px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] flex items-center justify-between gap-4 max-w-[240px] sm:max-w-[410px] w-full">
            <div className="flex -space-x-3" aria-hidden="true">
              {avatars.map((src, index) => (
                <CdnImage
                  key={index}
                  src={src}
                  alt={`Polymarket copy trader using FrenFlow bot ${index + 1}`}
                  width={32}
                  height={32}
                  className="border-2 border-white dark:border-[#18181D] rounded-[5px]"
                />
              ))}
            </div>
            <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-300 text-center sm:text-left">
              <strong>4.9/5</strong>{" "}
              <span className="sm:hidden">{t("copytrading.hero.rating.short")}</span>
              <span className="hidden sm:inline">{t("copytrading.hero.rating.long")}</span>
            </div>
          </div>
        </div>

        {/* Dashboard video */}
        <div className="relative flex justify-center mt-10 z-[5]">
          <div className="relative inline-block overflow-hidden max-w-[900px] w-full rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              title="FrenFlow Polymarket Copy Trading Bot Dashboard Demo"
              aria-label="Demo video showing FrenFlow automated copy trading bot for Polymarket prediction markets"
              className="w-full h-auto"
            >
              <source src="/examples/Frenflow-Polymarket.mp4" type="video/mp4" />
              Your browser does not support video. Watch how FrenFlow copy trading bot automatically replicates trades from top Polymarket traders.
            </video>
          </div>
        </div>

        {/* Bottom SEO text */}
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-8 text-center max-w-2xl mx-auto">
          The fastest Polymarket copy trading bot. Mempool-level detection, same-block execution, and sub-second latency. Track whale wallets and auto-copy 24/7.
        </p>
      </div>
    </section>
  );
}
